import { CloseOutlined } from '@ant-design/icons';
import { Button, Heading } from '@mm/components';

import { SlideoutMenu } from '../../components/SlideoutMenu';
import React, { useState } from 'react';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './SlideoutSelectAutomationAudience.module.css';
import {
  AudienceListSidebar,
  AudiencesModelWithDestroy,
} from '../../../../features/beta/modules/Audiences/AudienceListSidebar';
import { AudiencePreviewLazy } from '../../../../features/beta/modules/Audiences/AudienceSlideoutMenu/AudiencePreviewLazy/AudiencePreviewLazy';
import { FilterBar } from '../../../../features/beta/modules/Audiences/FilterBar/FilterBar';
import { isEqual } from 'lodash';
import { FilterParams, TagsFilters } from '@mm/state/model/AudiencesModel';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { useSelector } from 'react-redux';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { selectContacts } from '@mm/state/selectors/contactsSelector';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_SMALL } from '../../../../constants/screenSize';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { CampaignType } from '@mm/state/model/CampaignsModel';
import { getUniqId } from '@mm/utils';
import { Menu } from '../../../../features/beta/modules/Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { useInputTriggerCRUD } from '../../../../features/beta/modules/Automations/AutomationEdit/InputTriggers/use-input-trigger-crud';
import { MyLeadsAttributes } from '@mm/state/interfaces/myLeads';
import { getESStructure } from '../../../../features/beta/modules/Audiences/Audiences';

export type SlideoutSelectAutomationAudienceProps = {
  campaign: CampaignType;
};

export const SlideoutSelectAutomationAudience = (
  props: SlideoutSelectAutomationAudienceProps,
) => {
  const { campaign } = props;

  const { openNewAudienceNamePromptModal } = useModalActions();
  const { create, update } = useAudiencesCRUDActions();
  const campaignCRUDActions = useCampaignCRUDActions();
  const inputTriggerCRUD = useInputTriggerCRUD();
  const { screenWidth } = useResolutionInfo();
  const audiences = useSelector(selectAudienceSorted).filter(a => !a.hidden);
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const allContacts = useSelector(selectContacts);
  const botId = useSelector(activeChatbotIdSelector);
  const [activeAudienceId, setActiveAudienceId] = useState<number | undefined>(
    undefined,
  );
  const activeAudience = audiences.find(
    audience => audience.id === activeAudienceId,
  );
  const isMobile = screenWidth <= MOBILE_SMALL;

  const campaignAudAttributesWithGroupName =
    campaign.campaignAudiencesAttributes?.filter(attr => attr.groupName);

  const campaignAudience = (
    campaignAudAttributesWithGroupName?.length
      ? campaign?.audiences?.filter(audience =>
          campaignAudAttributesWithGroupName.find(
            attr => attr.audienceId === audience.id,
          ),
        )
      : []
  ) as AudiencesModelWithDestroy[];

  const [selectedAudiences, setSelectedAudiences] =
    useState<AudiencesModelWithDestroy[]>(campaignAudience);
  const [contactFilters, setContactFilters] = useState<{
    filterParams?: FilterParams;
    tagsFilters?: TagsFilters;
    esFilters?: MyLeadsAttributes;
    page?: number;
  }>({});
  const audienceSavePending = Boolean(
    contactFilters.tagsFilters || contactFilters.filterParams,
  );

  const onFiltersUpdated = (
    filterParams: FilterParams | undefined,
    tagsFilters: TagsFilters | undefined,
    esFilters: MyLeadsAttributes | undefined,
  ) => {
    const needsUpdate = !(
      isEqual(filterParams, contactFilters.filterParams) &&
      isEqual(tagsFilters, contactFilters.tagsFilters) &&
      isEqual(esFilters, contactFilters.esFilters)
    );
    if (needsUpdate) {
      setContactFilters({
        filterParams,
        tagsFilters,
        esFilters: getESStructure(esFilters),
      });
    }
  };

  const onSaveAudienceAs = () => {
    openNewAudienceNamePromptModal({
      onConfirmHandler: async name => {
        const newAudience = await create(
          {
            name,
            filterParams: contactFilters.filterParams,
            tagsFilters: contactFilters.tagsFilters,
            esFilters: contactFilters.esFilters,
          },
          activeChatbotId,
        );
        setContactFilters({});
        setActiveAudienceId(newAudience?.id);
      },
    });
  };

  const onUpdateAudience = async () => {
    if (activeAudience) {
      await update(
        {
          ...activeAudience,
          filterParams:
            contactFilters.filterParams || activeAudience.filterParams,
          tagsFilters: contactFilters.tagsFilters || activeAudience.tagsFilters,
          esFilters: getESStructure(
            contactFilters.esFilters || activeAudience.esFilters,
          ),
        },
        activeChatbotId,
      );
      setContactFilters({});
    }
  };

  const updateCampaign = async () => {
    const hash = btoa(getUniqId().toString());
    const existingGroupName = campaignAudAttributesWithGroupName?.find(
      a => a.groupName,
    )?.groupName;

    const savedAudiencesToDestroy = campaignAudience
      .filter(
        savedAudience =>
          !selectedAudiences.find(
            selectedAudience => savedAudience.id === selectedAudience.id,
          ),
      )
      .map(savedAdueince => {
        return {
          audienceId: savedAdueince.id,
          kind: 'included',
          groupName: existingGroupName
            ? existingGroupName
            : `${campaign.id}_${hash}`,
          destroy: true,
        };
      });

    const mappedAudiences = selectedAudiences.map(audience => {
      return {
        audienceId: audience.id,
        kind: 'included',
        groupName: existingGroupName
          ? existingGroupName
          : `${campaign.id}_${hash}`,
        destroy: audience.destroy,
      };
    });

    await campaignCRUDActions.updateCampaign(
      {
        id: campaign.id,
        campaignAudiencesAttributes: savedAudiencesToDestroy?.length
          ? [...mappedAudiences, ...savedAudiencesToDestroy]
          : [...mappedAudiences],
      } as CampaignType,
      botId,
    );
    closeSlideout();
  };

  const onDelete = () => {
    if (campaign && campaignAudAttributesWithGroupName) {
      inputTriggerCRUD.removeAudiencesTrigger({
        campaign,
        audienceIds: campaignAudAttributesWithGroupName.map(
          attr => attr.audienceId,
        ),
      });
    }
    closeSlideout();
  };

  const actionsMenu = [
    {
      disabled: false,
      children: 'Delete',
      onClick: onDelete,
    },
  ];

  const hasFilters =
    Object.keys(activeAudience?.filterParams || {}).length ||
    Object.keys(activeAudience?.tagsFilters || {}).length ||
    Object.keys(activeAudience?.esFilters || {}).length;

  const onClose = () => onBeforeClose(campaignAudience, selectedAudiences);

  const close = async () => {
    try {
      await onClose();
      closeSlideout();
    } catch (err) {}
  };

  const {
    setRef,
    close: closeSlideout,
    onBeforeClose,
  } = useSlideoutOnClose(onClose);

  return (
    <SlideoutMenu className={styles.container} setRef={setRef}>
      <div className={styles.header}>
        <Heading size={3} className={styles.heading}>
          Audience
        </Heading>

        <div className={styles.buttons}>
          <Button css={{ container: styles.save }} onClick={updateCampaign}>
            Save
          </Button>
          <Menu
            menu={actionsMenu}
            direction="bottom"
            useThreeDotButton
            className={styles.actionMenu}
          />
          <span className={styles.close} onClick={close}>
            <CloseOutlined />
          </span>
        </div>
      </div>
      <SlideoutMenu.Content className={styles.content}>
        <AudienceListSidebar
          selectedAudienceId={activeAudienceId}
          navigateToAudience={id => setActiveAudienceId(id)}
          className={styles.audienceList}
          onAudienceSelect={setSelectedAudiences}
          initialAudiences={selectedAudiences}
          withoutSystemFilters
          usedInAutomation
        />
        {!isMobile && (
          <div className={styles.contactsContainer}>
            <div className={styles.activeAudience}>
              {activeAudience?.name || 'All Contacts'}
              <span className={styles.activeAudienceCount}>
                {activeAudience
                  ? activeAudience?.contactsCount
                  : allContacts.pagination.total}{' '}
                contacts
              </span>
            </div>
            <FilterBar
              activeAudience={activeAudience}
              onFiltersUpdated={onFiltersUpdated}
              css={{
                filterBarItem: styles.filterBarItem,
                addFilterButton:
                  hasFilters || audienceSavePending
                    ? styles.addFilterButton
                    : styles.save,
                filterBarItemDeleteContainer: styles.filterDeleteContainer,
                filterBarItemDeleteButton: styles.filterDeleteButton,
                filterBarItemComponent: styles.filterBarItemComponent,
              }}
              plusIconColor={
                hasFilters || audienceSavePending ? '#262626' : 'white'
              }
              withReset
            />
            {audienceSavePending && (
              <div className={styles.buttons}>
                {activeAudienceId && (
                  <Button
                    color="secondary"
                    onClick={onUpdateAudience}
                    css={{ container: styles.secondaryButton }}
                  >
                    Save
                  </Button>
                )}
                <Button
                  color="secondary"
                  onClick={onSaveAudienceAs}
                  css={{ container: styles.secondaryButton }}
                >
                  Save as new audience
                </Button>
              </div>
            )}
            <AudiencePreviewLazy
              audienceId={activeAudience?.id}
              className={styles.audiencePreview}
              contactFilters={contactFilters}
              usedInAutomation
            />
          </div>
        )}
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
